import { Component, OnInit, OnDestroy, PLATFORM_ID, Inject, Renderer2 } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RuntimeEnvironmentService } from '@app/core/runtime-environment.service';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Route, Router } from '@angular/router';
import * as moment from 'moment';
import 'moment/locale/ru';
import { BsLocaleService, BsModalRef, BsModalService, defineLocale } from 'ngx-bootstrap';
import { ruLocale } from 'ngx-bootstrap/locale';
import { Subscription } from 'rxjs/Subscription';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { RavenInstallService } from '@app/core/raven/raven-install.service';
import { Angulartics2GoogleTagManager } from 'angulartics2/gtm';
import { GoogleTagManagerService } from '@app/core/angulartics/google-tag-manager.service';
import { LockScrollService } from '@app/shared/lock-scroll.service';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/mergeMap';
import { AuthService, CheckpointService, FullPageService } from '@app/core/services';
import { User } from '@app/entity';
import { AuthGuard } from '@app/core/auth-guard.service';
import { navigateToAuth } from '@app/shared/utils';
import { Observer } from 'rxjs/Observer';
import { animations } from '@app/app.animations.component';
import { PollModalsComponent } from '@app/shared/poll-modals/poll-modals.component';
import { MODAL_COUNT_KEY, Poll } from '@app/entity/poll';
import { PollService } from '@app/core/services/poll.service';
import { delay } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie';

@Component({
  selector: 'app-root',
  animations: animations,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Ветераны РФ';
  titleSeparator = ' - ';

  private routerSubscription: Subscription;

  checkpointObserver: Observer<boolean> = null;
  isFullPage = false;
  isLoading = true;

  isScrollLocked: boolean;

  user: User;

  nextUri: string;

  modalRef: BsModalRef;

  isBrowser = isPlatformBrowser(this.platformId);

  constructor(
    private titleService: Title,
    private router: Router,
    private route: ActivatedRoute,
    private checkpoint: CheckpointService,
    private authService: AuthService,
    private renderer: Renderer2,
    private fullPageService: FullPageService,
    private bsLocaleService: BsLocaleService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    // The services below should be *just* created, hence the unused imports.
    private runtimeEnvironmentService: RuntimeEnvironmentService,
    private ravenInstallService: RavenInstallService,
    private angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
    private googleTagManagerService: GoogleTagManagerService,
    private lockScrollService: LockScrollService,
    private modalService: BsModalService,
    private pollService: PollService,
    private cookieService: CookieService,
  ) {
    moment.updateLocale('ru', {
      calendar: { lastWeek: 'DD.MM.YYYY', nextWeek: 'dddd, DD.MM.YYYY' }
    });
    moment.locale('ru');
    defineLocale('ru', ruLocale);
    bsLocaleService.use('ru');
  }

  ngOnInit() {
    // Workaround: scroll to top on route change
    // https://github.com/angular/angular/issues/7791#issuecomment-268227536
    this.routerSubscription = this.router.events
      .filter(event => event instanceof NavigationEnd)
      .subscribe((event: NavigationEnd) => {
        if (this.user &&
            this.user.missing_required_fields.length &&
            this.user.user_type === 'veteran' &&
            this.router.url !== '/agreement') {
          this.checkpoint.createMissingFieldsCheckpoint(this.user);
        }

        if (this.isBrowser
          && !event.url.match(/#/)) {  // leave urls with hashes alone
          window.scrollTo(0, 0);
        }
      });

    this.lockScrollService.isScrollLocked
      .subscribe(locked => {
        this.isScrollLocked = locked;
        if (locked) {
          this.renderer.addClass(this.document.body, 'no-scroll');
        } else {
          this.renderer.removeClass(this.document.body, 'no-scroll');
        }
      });

    this.authService.user
      .subscribe(user => {
        if (this.checkpointObserver && user) {
          this.resolveCheckpoint(true);
        }
        this.user = user;
      });

    this.authService.userId
      .subscribe(userId => {
        if (userId) return;

        // If this is a logout (userId is falsy), then we should determine
        // whether any of the Routes in the active route's tree contains
        // `AuthGuard` in the `canActivate` list. And if so, it means that
        // the current page is not allowed to be viewed by an unauthorized user.
        // So we should redirect out from here.

        // Actually `this.route.snapshot` seems to be always equal to the `.root`
        const nodes: ActivatedRouteSnapshot[] = [this.route.snapshot.root];

        while (nodes.length) {  // BFS traverse
          const node = nodes.shift();  // pop a first node
          nodes.push(...node.children);  // should be a single route
          const route: Route = node.routeConfig;
          if (route && route.canActivate) {
            if (route.canActivate.find(cls => cls === AuthGuard)) {
              navigateToAuth(this.router, this.checkpoint);
              return;
            }
          }
        }
      });

    this.checkpoint.observer
      .subscribe(observer => {
        this.checkpointObserver = observer;
        if (observer != null) this.router.navigateByUrl('/auth/login');
      });

    this.checkpoint.missingFieldsObserver
      .subscribe(observer => {
        if (observer && this.checkpoint.user) this.router.navigateByUrl(`/auth/info`);
      });

    this.fullPageService.isFullPage
      .subscribe(fp => this.isFullPage = fp);

    this.route.queryParams
      .map(p => p['next'])
      .subscribe(next => this.nextUri = next);

    setTimeout(() => this.isLoading = false, 1500);

    this.getPolls();
  }

  private getModalId(): number {
    return +this.cookieService.get(MODAL_COUNT_KEY) || 0;
  }

  private setModalId(id) {
    this.cookieService.put(MODAL_COUNT_KEY, id, { expires: moment().add(1, 'months').toDate()});
  }

  getPolls() {
    if (this.isBrowser) {
      this.pollService.getPolls()
        .pipe(delay(15000))
        .subscribe(result => {
          const activePoll = result.results.find(poll => poll.is_active);
          if (activePoll && activePoll.id !== this.getModalId()) {
            this.modalShow(activePoll);
            this.setModalId(activePoll.id);
          }
        });
    }
  }

  ngOnDestroy() {
    if (this.routerSubscription) this.routerSubscription.unsubscribe();
  }

  get url() {
    return this.router.url;
  }

  onRouterOutletActivate(component: any) {
    const titleParts = [this.title];
    if (component.title) {
      titleParts.unshift(component.title);
    }
    this.titleService.setTitle(titleParts.join(this.titleSeparator));
  }

  resolveCheckpoint(result = false) {
    this.checkpointObserver.next(result);
    this.checkpointObserver.complete();
    this.checkpointObserver = null;
    if (this.isScrollLocked) this.lockScrollService.releaseScrollLock();

  }

  successfullyLoggedIn() {
    if (this.user && this.user.user_type === 'veteran') {
      this.router.navigateByUrl('/veteran/edit');
      return;
    }

    // next is expected to be url-encoded (router.url is encoded) - that is a URI,
    // but navigate expects unencoded path (IRI) and does url-encoding one more time.
    this.router.navigateByUrl(decodeURIComponent(this.nextUri || '/'));
  }

  modalShow(poll: Poll) {
    if (this.isBrowser) {
      const initialState = {
        poll: poll,
      };
      this.modalRef = this.modalService.show(PollModalsComponent, {initialState, class: 'poll-modal modal-lg'});
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts